import type { EditAfterSendPayload } from '@getaccept/lib-shared/src/pusher/contextual-commenting/contextual-commenting-payloads';
import type { Channel } from '@getaccept/pusher';
import { EditorEvent, DocumentEvent, RecipientEvent } from '@getaccept/pusher';
import type { ComputedRef, Ref } from '@vue/composition-api';
import { onBeforeUnmount, ref, watch } from '@vue/composition-api';
import { storeToRefs } from 'pinia';
import { pusherClient } from '../../../pusher';
import { usePusherStore } from '../../../pusher/store/pusher.store';
import { useContextualCommentingPusherData } from './contextual-commenting-pusher-data.composable';

export const useContextualCommentingPusher = (
  showContextualCommenting: boolean,
  contentIds: ComputedRef<string[]>
) => {
  const pusherStore = usePusherStore();
  const { editorChannels, documentChannel, recipientChannel } = storeToRefs(pusherStore);

  const {
    onCommentUpdated,
    onCommentRead,
    onReplyRead,
    onCommentCreated,
    onCommentDeleted,
  } = useContextualCommentingPusherData();

  const lockedContentIds: Ref<string[]> = ref([]);

  const resetLockedContentIds = (payload: EditAfterSendPayload) => {
    if (payload.editAfterSendIsActive) {
      return;
    }
    lockedContentIds.value = [];
  };

  const addDocumentListeners = () => {
    pusherClient.listen(
      documentChannel.value as Channel,
      DocumentEvent.EditAfterSend,
      resetLockedContentIds
    );
    pusherClient.listen(
      documentChannel.value as Channel,
      DocumentEvent.ContextualCommentReplyCreated,
      onCommentUpdated
    );
    pusherClient.listen(
      documentChannel.value as Channel,
      DocumentEvent.ContextualCommentUpdated,
      onCommentUpdated
    );
    pusherClient.listen(
      documentChannel.value as Channel,
      DocumentEvent.ContextualCommentRead,
      onCommentRead
    );
    pusherClient.listen(
      documentChannel.value as Channel,
      DocumentEvent.ContextualCommentReplyRead,
      onReplyRead
    );
  };

  const addRecipientListeners = () => {
    pusherClient.listen(
      recipientChannel.value as Channel,
      RecipientEvent.ContextualCommentCreated,
      onCommentCreated
    );
    pusherClient.listen(
      recipientChannel.value as Channel,
      RecipientEvent.ContextualCommentUpdated,
      onCommentUpdated
    );
    pusherClient.listen(
      recipientChannel.value as Channel,
      RecipientEvent.ContextualCommentDeleted,
      onCommentDeleted
    );
  };

  const removeListeners = () => {
    pusherClient.unbind(
      documentChannel.value as Channel,
      DocumentEvent.EditAfterSend,
      resetLockedContentIds
    );
    pusherClient.unbind(
      documentChannel.value as Channel,
      DocumentEvent.ContextualCommentReplyCreated,
      onCommentUpdated
    );
    pusherClient.unbind(
      documentChannel.value as Channel,
      DocumentEvent.ContextualCommentUpdated,
      onCommentUpdated
    );
    pusherClient.unbind(
      documentChannel.value as Channel,
      DocumentEvent.ContextualCommentRead,
      onCommentRead
    );
    pusherClient.unbind(
      documentChannel.value as Channel,
      DocumentEvent.ContextualCommentReplyRead,
      onReplyRead
    );

    pusherClient.unbind(
      recipientChannel.value as Channel,
      RecipientEvent.ContextualCommentCreated,
      onCommentCreated
    );
    pusherClient.unbind(
      recipientChannel.value as Channel,
      RecipientEvent.ContextualCommentUpdated,
      onCommentUpdated
    );
    pusherClient.unbind(
      recipientChannel.value as Channel,
      RecipientEvent.ContextualCommentDeleted,
      onCommentDeleted
    );
  };

  const editorUpdated = (id: string) => {
    if (lockedContentIds.value.includes(id)) {
      return;
    }
    lockedContentIds.value.push(id);
  };

  const addEditorListeners = async () => {
    await pusherStore.subscribeToEditorChannels(contentIds.value);
    editorChannels.value.forEach((editorChannel: Channel) => {
      pusherClient.listen(editorChannel, EditorEvent.Updated, ({ contentId }) =>
        editorUpdated(contentId)
      );
    });
  };

  const removeEditorListeners = () => {
    editorChannels.value.forEach((editorChannel: Channel) =>
      editorChannel.unbind(EditorEvent.Updated, ({ contentId }) => editorUpdated(contentId))
    );
    pusherStore.unsubscribeFromEditorChannels();
  };

  watch(
    [documentChannel, recipientChannel],
    () => {
      if (!showContextualCommenting) {
        return;
      }
      if (documentChannel.value) {
        addDocumentListeners();
      }
      if (recipientChannel.value) {
        addRecipientListeners();
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    removeListeners();
    removeEditorListeners();
  });

  watch(
    contentIds,
    (newIds: string[], oldIds: string[]) => {
      if ((!newIds.length && !oldIds.length) || !showContextualCommenting) {
        return;
      }
      if (newIds.length) {
        removeEditorListeners();
        addEditorListeners();
        return;
      }
      removeEditorListeners();
    },
    { immediate: true }
  );

  return {
    lockedContentIds,
  };
};
